import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import type { FlowNode } from "@/components/DataFlowchart";
import { ArrowRight, BarChart3, PieChart, Activity, Database, GitBranch, Target } from "lucide-react";
import EditorialSteps from "@/components/EditorialSteps";
import TechnologyStack from "@/components/TechnologyStack";
import CapabilityMatrix from "@/components/CapabilityMatrix";
import DataFlowchart from "@/components/DataFlowchart";
import { usePageContent } from "@/hooks/usePageContent";
import { 
  heroVariant, 
  sectionVariant, 
  staggerContainer, 
  viewportOnce,
} from "@/lib/motion";

const iconMap: Record<string, any> = { BarChart3, PieChart, Activity, Database, GitBranch, Target };

const fallback = {
  label: "Analytics",
  headline: "Clarity over complexity.",
  description: "Data should inform decisions, not overwhelm them. We build analytics systems that answer the questions that matter.",
  matrixTitle: "Analytics capabilities that drive decisions.",
  matrixDescription: "Not just data. Structured intelligence that enables action.",
  matrixCategories: [
    {
      id: "measurement", name: "Measurement & Attribution",
      capabilities: [
        { id: "mta", name: "Multi-Touch Attribution", icon: "GitBranch", description: "Understand the full customer journey across all touchpoints.", outcomes: ["Complete visibility into channel contributions", "Accurate CAC by channel and campaign", "Journey-based audience insights"], decisions: ["Where to allocate incremental budget", "Which touchpoints to optimize", "How to sequence messaging"], failuresPrevented: ["Over-crediting last-click channels", "Cutting high-assist channels", "Misattributing conversions"] },
        { id: "incrementality", name: "Incrementality Testing", icon: "Target", description: "Prove true marketing impact through controlled experiments.", outcomes: ["Causal proof of marketing impact", "True incremental lift measurement", "Channel-level ROI validation"], decisions: ["Which channels drive true incremental value", "Optimal spend levels by channel", "When to scale or cut investment"], failuresPrevented: ["Paying for conversions that would happen anyway", "Scaling unprofitable channels", "Wrong budget allocation"] },
        { id: "mmm", name: "Marketing Mix Modeling", icon: "PieChart", description: "Optimize budget allocation across your entire portfolio.", outcomes: ["Data-driven budget recommendations", "Saturation curve visibility", "Scenario planning capability"], decisions: ["Annual budget allocation", "Diminishing returns thresholds", "New channel investment sizing"], failuresPrevented: ["Over-investing past saturation", "Under-investing in high-potential channels", "Reactive vs. strategic budgeting"] },
      ],
    },
    {
      id: "infrastructure", name: "Data Infrastructure",
      capabilities: [
        { id: "tracking", name: "First-Party Data Strategy", icon: "Database", description: "Build privacy-compliant tracking for a cookieless world.", outcomes: ["GDPR/CCPA-compliant data collection", "Server-side tracking implementation", "First-party data enrichment"], decisions: ["What data to collect and how", "Consent management approach", "Identity resolution strategy"], failuresPrevented: ["Data loss from privacy changes", "Compliance violations", "Identity fragmentation"] },
        { id: "dashboards", name: "Real-Time Dashboards", icon: "BarChart3", description: "Live visibility into performance across all channels.", outcomes: ["Single source of truth", "Real-time performance visibility", "Self-serve reporting for stakeholders"], decisions: ["When to pause or scale campaigns", "Which tests to call", "Where to investigate anomalies"], failuresPrevented: ["Delayed response to issues", "Conflicting reports across teams", "Decision paralysis from data overload"] },
      ],
    },
  ],
  flowchartTitle: "How data becomes decisions.",
  flowchartDescription: "From raw signals to strategic action. The complete data journey.",
  flowchartNodes: [
    { id: "web", label: "Web Analytics", description: "GA4, Mixpanel, Amplitude", type: "source" },
    { id: "ads", label: "Ad Platforms", description: "Meta, Google, LinkedIn", type: "source" },
    { id: "crm", label: "CRM Data", description: "Salesforce, HubSpot", type: "source" },
    { id: "cdp", label: "CDP / Identity", description: "Segment, mParticle", type: "transform" },
    { id: "warehouse", label: "Data Warehouse", description: "BigQuery, Snowflake", type: "transform" },
    { id: "attribution", label: "Attribution Model", description: "Multi-touch, incrementality", type: "decision" },
    { id: "insights", label: "Insights Engine", description: "Automated anomaly detection", type: "decision" },
    { id: "budget", label: "Budget Optimization", description: "Channel allocation", type: "output" },
    { id: "audience", label: "Audience Actions", description: "Targeting, suppression", type: "output" },
  ],
  flowchartConnections: [
    { from: "web", to: "cdp" }, { from: "ads", to: "cdp" }, { from: "crm", to: "cdp" },
    { from: "cdp", to: "warehouse" }, { from: "warehouse", to: "attribution" },
    { from: "warehouse", to: "insights" }, { from: "attribution", to: "budget" },
    { from: "insights", to: "audience" },
  ],
  processTitle: "From data chaos to clarity.",
  processSteps: [
    { number: "01", title: "Data Audit", description: "Comprehensive review of your current tracking, data quality, and measurement gaps across all touchpoints." },
    { number: "02", title: "Architecture Design", description: "Design a unified data architecture that connects sources, enables attribution, and respects privacy." },
    { number: "03", title: "Implementation", description: "Deploy tracking, configure integrations, and build dashboards tailored to your business questions." },
    { number: "04", title: "Validation & Training", description: "Rigorous QA to ensure data accuracy, plus training so your team can self-serve insights." },
    { number: "05", title: "Optimization Loop", description: "Ongoing refinement based on new data, evolving business needs, and emerging opportunities." },
  ],
  techLabel: "Analytics Stack",
  techTitle: "Tools we work with.",
  techDescription: "We're platform-agnostic and work with the tools that best serve your analytics needs.",
  techCategories: [
    { title: "Web Analytics", icon: "BarChart3", items: ["GA4", "Adobe Analytics", "Mixpanel", "Amplitude"] },
    { title: "Visualization", icon: "PieChart", items: ["Looker", "Tableau", "Power BI", "Data Studio"] },
    { title: "Attribution", icon: "Activity", items: ["Triple Whale", "Northbeam", "Rockerbox", "Ruler"] },
    { title: "CDP & Tag", icon: "Database", items: ["Segment", "GTM", "Tealium", "mParticle"] },
  ],
  ctaText: "Unlock your data",
};

// Resolve icon strings from CMS to actual components
const resolveIcon = (iconName: string) => iconMap[iconName] || BarChart3;

const resolveMatrixCategories = (cats: any[]) =>
  cats.map((cat) => ({
    ...cat,
    capabilities: cat.capabilities.map((cap: any) => ({
      ...cap,
      icon: typeof cap.icon === "string" ? resolveIcon(cap.icon) : cap.icon,
    })),
  }));

const resolveTechCategories = (cats: any[]) =>
  cats.map((cat) => ({
    ...cat,
    icon: typeof cat.icon === "string" ? resolveIcon(cat.icon) : cat.icon,
  }));

const Analytics = () => {
  const { content } = usePageContent("analytics", fallback);

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 md:pt-28 lg:pt-32 pb-16 md:pb-20 lg:pb-24">
        <div className="container-wide">
          <motion.div className="max-w-2xl mb-12 md:mb-16 lg:mb-20" initial="hidden" animate="visible" variants={staggerContainer}>
            <motion.div variants={heroVariant} className="flex items-center gap-3 mb-6">
              <div className="w-8 h-px bg-primary" />
              <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-medium">{content.label}</span>
            </motion.div>
            <motion.h1 variants={heroVariant} className="font-display text-3xl md:text-4xl lg:text-5xl font-semibold tracking-tight text-foreground mb-6">{content.headline}</motion.h1>
            <motion.p variants={heroVariant} className="text-lg text-muted-foreground leading-relaxed">{content.description}</motion.p>
          </motion.div>
        </div>

        <CapabilityMatrix title={content.matrixTitle} description={content.matrixDescription} categories={resolveMatrixCategories(content.matrixCategories)} />

        <DataFlowchart title={content.flowchartTitle} description={content.flowchartDescription} nodes={content.flowchartNodes as FlowNode[]} connections={content.flowchartConnections} />

        <EditorialSteps title={content.processTitle} steps={content.processSteps} />

        <TechnologyStack label={content.techLabel} title={content.techTitle} description={content.techDescription} categories={resolveTechCategories(content.techCategories)} />

        <div className="container-wide">
          <motion.div className="mt-12 md:mt-16 lg:mt-20 text-center" initial="hidden" whileInView="visible" viewport={viewportOnce} variants={sectionVariant}>
            <Button size="lg" className="group" asChild>
              <Link to="/contact">{content.ctaText}<ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" /></Link>
            </Button>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Analytics;
